import React, { PropsWithChildren } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
    Extrapolation,
    SharedValue,
    interpolate,
    runOnJS,
    useAnimatedReaction,
    useAnimatedStyle,
} from 'react-native-reanimated';

type PickbarSheetSyncProps = PropsWithChildren<{
    animatedYIndex: SharedValue<number>;
    onSheetOpenChange: (isOpen: boolean) => void;
}>;

/**
 * Keeps children in sync with a bottom sheet (event details, player profile).
 * Interpolates translateY from the sheet's animated index so children follow the sheet as it translates up/down.
 * Fires `onSheetOpenChange` when the sheet reaches fully open or closed state.
 *
 * PickbarSheetSync (translateY synced to sheet)
 *   └── AnimatedPickbarContainer (spring enter/exit)
 *       └── pickbar content
 */
export const PickbarSheetSync = ({ children, animatedYIndex, onSheetOpenChange }: PickbarSheetSyncProps) => {
    useAnimatedReaction(
        () => animatedYIndex.value,
        value => {
            if (value >= 0) {
                return runOnJS(onSheetOpenChange)(true);
            }
            if (value <= -1) {
                return runOnJS(onSheetOpenChange)(false);
            }
        }
    );

    const { height } = useWindowDimensions();

    const translateStyle = useAnimatedStyle(() => {
        return {
            transform: [
                {
                    translateY: interpolate(animatedYIndex.value, [-1, 0], [height, 0], Extrapolation.CLAMP),
                },
            ],
        };
    });

    return (
        <Animated.View style={[translateStyle, StyleSheet.absoluteFill]} pointerEvents={'box-none'}>
            {children}
        </Animated.View>
    );
};
